import { err, ok, Result } from './Result';
import { ErrorDto } from '../dto/errorDto';
import { ModelBase } from '../models/model.base';
import { NoArgsConstructor } from '../types/utility-types';
import { DataSourceResult } from './data-source/data-source-result';

export function hasError<T>(results: Result<T, any>[]) {
  return results.some(result => !result.isValid);
}

export function getErrors<T>(results: Result<T, ErrorDto>[]): ErrorDto[] {
  return results.map(result => result.errors).flat();
}

export function extractValues<T>(results: Result<T, any>[]): T[] {
  return results.map(r => r.value as T);
}

export function checkAndMapToModel<TModel extends ModelBase<TDto>, TDto extends object>(
  result: Result<TDto, ErrorDto>,
  modelConstructor: NoArgsConstructor<TModel>,
  mutateCallback?: (model: TModel) => void
): Result<TModel, ErrorDto> {
  if (!result.isValid) {
    return err(result.errors);
  }

  const dto = result.value as TDto;
  const model = new modelConstructor().mapFrom(dto);
  if (mutateCallback) {
    mutateCallback(model);
  }

  return ok(model);
}

export function checkAndMapToModels<TModel extends ModelBase<TDto>, TDto>(
  result: Result<TDto[], ErrorDto>,
  modelConstructor: NoArgsConstructor<TModel>,
  mutateCallback?: (models: TModel[]) => void
): Result<TModel[], ErrorDto> {
  if (!result.isValid) {
    return err(result.errors);
  }
  const dtos = result.value as TDto[];
  const models = dtos.map(dto => new modelConstructor().mapFrom(dto));

  if (mutateCallback) {
    mutateCallback(models);
  }

  return ok(models);
}

export function checkAndMapToModelsFromDataSourceResult<TModel extends ModelBase<TDto>, TDto>(
  result: Result<DataSourceResult<TDto>, ErrorDto>,
  modelConstructor: NoArgsConstructor<TModel>,
  mutateCallback?: (models: TModel[]) => void
): Result<TModel[], ErrorDto> {
  if (!result.isValid) {
    return err(result.errors);
  }

  const dtos = (result.value as DataSourceResult<TDto>).items;
  const models = dtos.map(dto => new modelConstructor().mapFrom(dto));

  if (mutateCallback) {
    mutateCallback(models);
  }

  return ok(models);
}

export function allSucceeded(results: Result<any, any>[]): boolean {
  return results.every(r => r.isValid);
}

export function extractErrors<T>(results: Result<any, T>[]): T[] {
  return results.map(r => r.errors).flat();
}
